"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { platformFetch } from "@/lib/api/browser";
import { useIdentity } from "@/lib/api/session";
import { HISTORY_CAP, HISTORY_PAGE } from "./config";
import { createClientId } from "./ids";
import {
  ablyTransport,
  pollingTransport,
  type ChatConnection,
  type TransportStatus,
} from "./transport";
import type { Announcement, ChatEvent, ChatIdentity, ChatMessage } from "./types";

export type ChatRow = ChatMessage & { status?: "pending" | "failed" };

export type RoomOption = { id: string; label: string; channel: string };

type Session = {
  identity: ChatIdentity;
  backend: { durable: boolean; live: boolean };
  /* The admin's composer gate, from /api/chat/identity. Absent on payloads
     from a server built before it existed. */
  gate?: { minTierOrdinal: number };
};

const IDLE_TIMEOUT_MS = 15 * 60 * 1000;

const merge = (rows: ChatRow[], incoming: ChatRow) => {
  const byClientId = incoming.clientId
    ? rows.findIndex((row) => row.clientId === incoming.clientId && row.status === "pending")
    : -1;
  if (byClientId >= 0) {
    const next = rows.slice();
    next[byClientId] = incoming;
    return next;
  }

  if (rows.some((row) => row.id === incoming.id)) {
    return rows.map((row) => (row.id === incoming.id ? { ...row, ...incoming } : row));
  }

  const next = [...rows, incoming];
  next.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return next.length > HISTORY_CAP ? next.slice(next.length - HISTORY_CAP) : next;
};

export function useChat(roomId: string) {
  const [rows, setRows] = useState<ChatRow[]>([]);
  const [status, setStatus] = useState<TransportStatus>("connecting");
  const [session, setSession] = useState<Session | null>(null);
  const [rooms, setRooms] = useState<RoomOption[]>([]);
  const [announcement, setAnnouncement] = useState<Announcement>(null);
  const [cooldown, setCooldown] = useState(0);
  const [idle, setIdle] = useState(false);
  const [presence, setPresence] = useState<number | null>(null);

  const lastIdRef = useRef<string | undefined>(undefined);
  const rowsRef = useRef<ChatRow[]>([]);
  rowsRef.current = rows;

  const room = useMemo(() => rooms.find((entry) => entry.id === roomId), [rooms, roomId]);

  const apply = useCallback((event: ChatEvent) => {
    if (event.type === "delete") {
      setRows((current) =>
        current.map((row) =>
          row.id === event.id ? { ...row, deletedAt: new Date().toISOString() } : row,
        ),
      );
      return;
    }
    setRows((current) => merge(current, event.message));
    if (!lastIdRef.current || event.message.id > lastIdRef.current) {
      lastIdRef.current = event.message.id;
    }
  }, []);

  /* Sign-in happens elsewhere in the app and ends in a client-side navigation,
     so this panel is never remounted by it. Without watching the platform
     session, someone who had just signed in still saw "Sign in to chat" until
     they reloaded — signed in everywhere except the one place they were trying
     to talk. Signing out had the same problem in reverse. */
  const { identity: platformIdentity, isPending: identityPending } = useIdentity();
  const platformAccountId = platformIdentity?.id ?? null;

  useEffect(() => {
    if (identityPending) return;
    let cancelled = false;
    void (async () => {
      const identityResponse = await fetch("/api/chat/identity", { cache: "no-store" }).catch(
        () => null,
      );
      if (cancelled) return;
      const identityPayload = await identityResponse?.json().catch(() => null);
      let data = (identityPayload?.data ?? null) as Session | null;

      /* Signed in, yet a guest to the chat server: the platform keeps its
         session cookie on the API host, which never sends it here. The
         browser holds that session, so it fetches the platform's realtime
         URL and hands it over; the server opens it and lets the platform
         vouch for the account before it opens a chat session. */
      if (data?.identity.isGuest && platformAccountId) {
        const realtimeUrl = (
          await platformFetch<{ data: { url: string } }>("realtime").catch(() => null)
        )?.data?.url;
        if (cancelled) return;

        if (realtimeUrl) {
          const proved = await fetch("/api/chat/identity", {
            method: "POST",
            cache: "no-store",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ realtimeUrl, accountId: platformAccountId }),
          }).catch(() => null);
          if (cancelled) return;
          const provedPayload = await proved?.json().catch(() => null);
          if (provedPayload?.data) data = provedPayload.data as Session;
        }
      }

      if (data) setSession(data);

      const roomsResponse = await fetch("/api/chat/rooms", { cache: "no-store" }).catch(() => null);
      if (cancelled) return;
      const roomsPayload = await roomsResponse?.json().catch(() => null);
      if (roomsPayload?.data) {
        setRooms(roomsPayload.data.rooms ?? []);
        setAnnouncement(roomsPayload.data.announcement ?? null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [platformAccountId, identityPending]);

  const loadHistory = useCallback(async () => {
    const response = await fetch(
      `/api/chat/rooms/${roomId}/messages?limit=${HISTORY_PAGE}`,
      { cache: "no-store" },
    ).catch(() => null);
    if (!response?.ok) return;
    const payload = await response.json().catch(() => null);
    const history = (payload?.data?.messages ?? []) as ChatMessage[];
    setRows(history);
    lastIdRef.current = history.at(-1)?.id;
  }, [roomId]);

  useEffect(() => {
    setRows([]);
    lastIdRef.current = undefined;
    setIdle(false);
    setPresence(null);
  }, [roomId]);

  useEffect(() => {
    if (!session || !room || idle) return;

    let connection: ChatConnection | undefined;
    let disposed = false;

    const open = async () => {
      await loadHistory();
      if (disposed || document.visibilityState === "hidden") return;
      connection?.close();
      const transport = session.backend.live ? ablyTransport : pollingTransport;
      connection = transport({
        channel: room.channel,
        roomId,
        hidden: session.identity.isHidden,
        lastId: () => lastIdRef.current,
        onEvent: apply,
        onStatus: setStatus,
        onPresence: setPresence,
      });
    };

    void open();

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        connection?.close();
        connection = undefined;
      } else if (!connection && !disposed) {
        void open();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", onVisibility);
      connection?.close();
    };
  }, [session, room, roomId, idle, apply, loadHistory]);

  useEffect(() => {
    const timer = setTimeout(() => setIdle(true), IDLE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [roomId, rows.length]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((value) => Math.max(0, value - 1)), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const send = useCallback(
    async (body: string, options?: { reveal?: boolean }) => {
      const trimmed = body.trim();
      if (!trimmed || !session || session.identity.isGuest) return;
      const reveal = options?.reveal === true;

      const clientId = createClientId();
      const optimistic: ChatRow = {
        id: `pending-${clientId}`,
        roomId: room?.channel ?? roomId,
        siteId: "",
        kind: "user",
        body: trimmed,
        author: {
          accountId: session.identity.accountId,
          nickname: session.identity.nickname,
        },
        createdAt: new Date().toISOString(),
        clientId,
        status: "pending",
      };
      setRows((current) => [...current, optimistic]);

      const response = await fetch(`/api/chat/rooms/${roomId}/messages`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ body: trimmed, clientId, reveal }),
      }).catch(() => null);

      if (!response?.ok) {
        const payload = await response?.json().catch(() => null);
        const retryAfter = payload?.error?.details?.retryAfter;
        if (typeof retryAfter === "number") setCooldown(retryAfter);
        setRows((current) =>
          current.map((row) =>
            row.clientId === clientId ? { ...row, status: "failed" as const } : row,
          ),
        );
        return;
      }

      const payload = await response.json().catch(() => null);
      if (payload?.data?.message) apply({ type: "message", message: payload.data.message });
    },
    [apply, room, roomId, session],
  );

  const retry = useCallback(
    (clientId: string) => {
      const target = rowsRef.current.find((row) => row.clientId === clientId);
      if (!target) return;
      setRows((current) => current.filter((row) => row.clientId !== clientId));
      void send(target.body);
    },
    [send],
  );

  const resume = useCallback(() => setIdle(false), []);

  return {
    messages: rows,
    status: idle ? ("offline" as TransportStatus) : status,
    identity: session?.identity ?? null,
    backend: session?.backend ?? null,
    minTierOrdinal: session?.gate?.minTierOrdinal ?? 0,
    rooms,
    announcement,
    cooldown,
    idle,
    presence,
    send,
    retry,
    resume,
    /* A platform event is a prompt to refetch, not data — see
       use-platform-events.ts. This is what it prompts. */
    refresh: loadHistory,
  };
}
